import { consola } from 'consola'
import { useModalStore, modalStateEnum } from '@shared/model'
import { useUserProfileStore } from '@entities/user'
import { useLoyaltyStore } from '@entities/loyalty'
import { useInstitutionStore } from '@entities/institution'

const logger = consola.withTag('onboarding')

// Ключ «онбординг показан» храним per-user, чтобы показать модалку ровно один раз
// после регистрации и больше не дёргать пользователя при последующих входах.
const STORAGE_PREFIX = 'onboarding_seen_'

function storageKey(userId: string | number): string {
  return `${STORAGE_PREFIX}${userId}`
}

/** Видел ли пользователь онбординг-модалку (per-user, localStorage). */
export function hasSeenOnboarding(userId?: string | number | null): boolean {
  if (!import.meta.client || userId == null || userId === 0) return false
  try {
    return localStorage.getItem(storageKey(userId)) === '1'
  } catch {
    return false
  }
}

/** Помечает онбординг как просмотренный — больше не всплывёт у этого пользователя. */
export function markOnboardingSeen(userId?: string | number | null): void {
  if (!import.meta.client || userId == null || userId === 0) return
  try {
    localStorage.setItem(storageKey(userId), '1')
  } catch {
    /* localStorage недоступен (приватный режим и т.п.) — не критично */
  }
}

/**
 * Онбординг новичка после регистрации/подтверждения телефона.
 *
 * Модалка благодарит за регистрацию, показывает приветственные баллы и предлагает
 * сразу сохранить адрес доставки (закрывает пункт «дефолтный адрес» на старте).
 * Показывается ровно один раз — повторные вызовы для того же пользователя no-op.
 */
export function useOnboardingTrigger() {
  const modalStore = useModalStore()
  const userProfile = useUserProfileStore()
  const loyaltyStore = useLoyaltyStore()
  const institutionStore = useInstitutionStore()

  /**
   * Открывает онбординг-модалку, если пользователь её ещё не видел.
   * @returns true — модалка открыта; false — пропущено (уже видел / нет userId / SSR).
   */
  async function startOnboarding(): Promise<boolean> {
    if (!import.meta.client) return false

    const userId = userProfile.getUser?.id
    if (!userId) return false

    if (hasSeenOnboarding(userId)) {
      logger.debug('Онбординг уже показывался — пропускаем')
      return false
    }

    // Приветственная механика (баллы/лесенка скидок) живёт в данных заведения.
    // Онбординг всплывает сразу после регистрации/подтверждения телефона — раньше,
    // чем мог завершиться префетч заведения. Если оно ещё не загружено, модалка
    // откроется без блока лояльности (режим уйдёт в дефолтный bonus_points с 0 баллов).
    // Поэтому гарантированно подтягиваем заведение перед показом.
    if (!institutionStore.institution) {
      // institutionStore.currentId — мёртвый ref (всегда 1), грузил бы чужое
      // заведение. Берём ID из env-конфига деплоя (тот же источник, что и
      // префетч меню).
      const institutionId = Number(useRuntimeConfig().public.institutionID)
      try {
        if (institutionId > 0) await institutionStore.fetchBranches(institutionId)
      } catch (error) {
        logger.warn('Не удалось загрузить заведение для онбординга — покажем без лояльности', error)
      }
    }

    // У новичка адреса обычно нет, но подстрахуемся: если адрес уже есть — блок
    // «сохраните адрес» в модалке скрываем (показываем только благодарность + баллы).
    const hasAddress =
      (userProfile.getUserAddresses?.length ?? 0) > 0 ||
      userProfile.getSelectedDeliveryAddress != null

    // Режим приветствия активируется в админке и взаимоисключающий: либо приветственные
    // баллы, либо скидка на первые N заказов. Берём активный режим из конфига заведения.
    const institution = institutionStore.institution
    // Программа лояльности может быть вообще выключена — тогда в модалке только
    // благодарность + сохранение адреса (без баллов и лесенки).
    const loyaltyEnabled = institution?.loyalty_enabled !== false
    const welcomeMode = institution?.loyalty_welcome_mode ?? 'bonus_points'
    // Баллы уже начислены бэком и подтянуты в loyalty/профиль при логине.
    const welcomePoints =
      loyaltyStore.loyaltyInfo.points ||
      userProfile.getUser?.points ||
      Number(institution?.loyalty_welcome_bonus_points) ||
      0
    const firstOrdersCount = Number(institution?.loyalty_first_orders_count) || 0

    logger.info('Показываем онбординг новому пользователю', {
      userId,
      loyaltyEnabled,
      welcomeMode,
      welcomePoints,
      firstOrdersCount,
      hasAddress,
    })
    modalStore.open(modalStateEnum.Onboarding, {
      loyaltyEnabled,
      welcomeMode,
      welcomePoints,
      firstOrdersCount,
      hasAddress,
    })
    return true
  }

  return { startOnboarding, hasSeenOnboarding, markOnboardingSeen }
}
